import { Colors, FontSizePx, Spacing } from '@kvika/audur-theme'
import { GroupBase, StylesConfig } from 'react-select'
import styled, { css } from 'styled-components'

import { SelectWidth, Option } from '../Constants'

type Width = SelectWidth | string | undefined
type BackgroundColor = Colors | string | undefined
type Color = Colors | string | undefined

export const getWidth = (width: Width): string => {
	if (width === SelectWidth.SMALL) {
		return '160px'
	}
	if (width === SelectWidth.LARGE) {
		return '560px'
	}
	if (width === SelectWidth.MEDIUM || !width) {
		return '420px'
	}
	return width
}

export const getCustomStyles = ({
	width,
	backgroundColor,
	color,
	fontSize,
	textColor,
	labelOnly,
	fullSize,
}: {
	width: Width
	backgroundColor?: BackgroundColor
	color?: Color
	fontSize?: FontSizePx
	textColor?: Colors | string
	labelOnly?: boolean
	fullSize?: boolean
}): StylesConfig<Option, boolean, GroupBase<Option>> => {
	return {
		option: (provided, state) => {
			const getFocusedStyles = () => {
				if (!labelOnly) {
					return {
						backgroundColor: backgroundColor || Colors.Transparent,
						color: color || Colors.Black,
						fontWeight: 'bold',
					}
				}
				return {
					backgroundColor: Colors.SilverDark,
					color: Colors.Black,
				}
			}

			return {
				...provided,
				padding: '12px 24px 12px 24px',
				fontSize: '18px',
				lineHeight: '28px',
				...(state.isFocused && getFocusedStyles()),
				...(state.isSelected &&
					(!labelOnly
						? {
								backgroundColor: backgroundColor || Colors.Transparent,
								color: color || Colors.Black,
								fontWeight: 'bold',
							}
						: {
								fontWeight: 'bold',
								backgroundColor: Colors.SilverDark,
								color: Colors.Black,
							})),
				...(!state.isSelected && {
					'&:hover': {
						backgroundColor: labelOnly ? Colors.SilverDark : Colors.Transparent,
						fontWeight: !labelOnly ? 'bold' : 400,
					},
				}),
				cursor: 'pointer',
			}
		},
		singleValue: (provided) => {
			return { ...provided, color: textColor || Colors.Beige, fontSize: FontSizePx.H2, lineHeight: '28px' }
		},

		noOptionsMessage: (provided) => {
			return { ...provided, color: textColor || Colors.Black }
		},
		placeholder: (provided) => {
			return { ...provided, color: textColor || Colors.LightGray }
		},
		multiValue: (provided, state) => {
			const { value } = state.selectProps
			const addComma = Array.isArray(value) && state.index < value.length - 1

			return {
				...provided,
				backgroundColor: 'transparent',
				color: textColor || Colors.Beige,
				lineHeight: FontSizePx.H2,
				fontSize: FontSizePx.H2,
				margin: 0,
				div: {
					padding: 0,
				},
				...(addComma && {
					'&:after': {
						paddingRight: Spacing.XSmall,
						content: "','",
					},
				}),
				'> div': {
					fontSize: FontSizePx.H2,
					lineHeight: '28px',
					color: textColor || Colors.Beige,
				},
			}
		},
		multiValueLabel: (provided) => {
			return { ...provided, color: textColor || Colors.SilverDark }
		},
		menu: (provided) => {
			return {
				...provided,
				backgroundColor: Colors.Beige,
				borderRadius: '20px',
				padding: '12px 0',
				width: labelOnly ? (fullSize ? 'calc(100vw - 32px)' : 'auto') : getWidth(width),
				minWidth: '206px',
				zIndex: 1000,
				...(fullSize ? { marginLeft: '16px', marginRight: '16px' } : {}),
			}
		},
		menuPortal: (provided) => {
			return {
				...provided,
				top: 'auto',
				...(fullSize ? { left: '0px', width: 'calc(100vw - 32px)' } : {}),
			}
		},
		menuList: (provided) => {
			return { ...provided }
		},
		control: (provided, state) => {
			return {
				...provided,
				minHeight: labelOnly ? '0px' : '48px',
				border: 'none',
				borderBottomWidth: labelOnly ? '0px' : '2px',
				borderBottomColor: Colors.LightGray,
				borderBottomStyle: 'solid',
				borderRadius: 'none',
				background: 'transparent',
				fontSize: fontSize || FontSizePx.Body,
				...(labelOnly && {
					height: '0px',
					minHeight: '0px',
					border: 'none',
					background: 'transparent',
					pointerEvents: 'none',
				}),
				...(!labelOnly && {
					':hover': {
						borderBottomColor: Colors.Beige,
					},
					':active': {
						borderBottomColor: Colors.Beige,
					},
				}),
				...(state.isFocused &&
					!labelOnly && {
						boxShadow: 'none',
						borderBottomColor: Colors.Beige,
					}),
				...(state.isDisabled && {
					borderColor: Colors.LightGray,
					backgroundColor: Colors.LightCardGray,
				}),
			}
		},
		container: (provided) => {
			return {
				...provided,
				width: getWidth(width),
				'& > label': {
					color: `${Colors.SilverDark} !important`,
				},
			}
		},
		indicatorSeparator: () => {
			return {}
		},
		multiValueRemove: () => {
			return {
				display: 'none',
			}
		},
		clearIndicator: () => {
			return {
				display: 'none',
			}
		},
		valueContainer: (provided) => {
			return {
				...provided,
				display: labelOnly ? 'none' : 'flex',
				paddingLeft: 0,
				color: Colors.Beige,
				fontSize: FontSizePx.H2,
				'div:nth-last-of-type(2)': {
					width: 'fit-content',
				},
			}
		},
		indicatorsContainer: (provided) => {
			return {
				...provided,
				display: labelOnly ? 'none' : 'flex',
			}
		},
		input: (provided) => {
			return {
				...provided,
				color: textColor || Colors.Beige,
				height: labelOnly ? '0px' : 'auto',
				input: {
					height: labelOnly ? '0px' : 'auto',
				},
				'::after': {
					height: labelOnly ? '0px' : 'auto',
				},
			}
		},
	}
}

export const SelectWrapper = styled.div<{ isKeyboardFocused: boolean; hidePadding: boolean }>`
	padding: ${(props) => (props.hidePadding ? '10px 0' : '10px')};
	${(props) =>
		props.isKeyboardFocused &&
		css`
			border: 2px solid ${Colors.SilverDark};
			border-radius: 8px;
			padding: 8.4px;
		`}
`
